import { useEffect, useRef, useState } from "react";

/**
 * A local draft of a server value that the user edits in small steps (steppers, text): shown at
 * once, sent after a short pause, and replaced by the server's value whenever that changes and
 * no edit is pending. If the server refuses the edit, the draft goes back to the server's value.
 * A pending edit is sent at once when the component unmounts.
 */
export function useDraft<T>(
  remote: T,
  commit: (value: T) => Promise<boolean> | void,
  delayMs = 400,
): [T, (value: T) => void] {
  const [draft, setDraft] = useState(remote);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<{ value: T; commit: typeof commit; remote: T }>({
    value: remote,
    commit,
    remote,
  });
  latest.current.commit = commit;
  latest.current.remote = remote;
  const key = JSON.stringify(remote);

  useEffect(() => {
    if (timer.current === null) setDraft(latest.current.remote);
  }, [key]);

  const flush = () => {
    timer.current = null;
    const result = latest.current.commit(latest.current.value);
    if (result instanceof Promise) {
      void result.then((ok) => {
        if (!ok && timer.current === null) setDraft(latest.current.remote);
      });
    }
  };

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        flush();
      }
    },
    [],
  );

  const update = (value: T) => {
    latest.current.value = value;
    setDraft(value);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(flush, delayMs);
  };
  return [draft, update];
}
